import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Group } from '../../../models/Group';
import { GroupsService } from '../../../services/groups.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-groups-list',
  templateUrl: './groups-list.component.html',
  styleUrls: ['./groups-list.component.css']
})
export class GroupsListComponent implements OnInit {
  groups: Group[] = [];
  loading = false;
  errorMessage: string | null = null;

  searchTerm = '';
  
  // Controles de paginação
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  columns: { titulo: string, label: string, formatter?: (value: any, item?: any) => string }[] = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Descrição', label: 'description' },
    { titulo: 'Data Criação', label: 'createdAt', formatter: (value: any) => this.formatDate(value) },
    { titulo: 'Atualizado Por (ID)', label: 'updatedBy', formatter: (value: any) => value || 'N/A' },
    { titulo: 'Última Atualização', label: 'updatedAt', formatter: (value: any) => this.formatDate(value) }
  ];

  actions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-warning'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-danger'
    }
  ];

  constructor(
    private router: Router,
    private groupsService: GroupsService,
    private errorService: ErrorService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadGroups();
  }

  /**
   * Carrega os grupos com paginação
   */
  loadGroups(): void {
    this.loading = true;
    this.errorMessage = null;

    this.groupsService.getGroups(this.currentPage, this.pageSize).subscribe({
      next: (response) => {
        this.groups = response.data.map(dto => ({
          id: dto.Id,
          name: dto.Name,
          description: dto.Description,
          profileId: dto.ProfileId,
          createdAt: new Date(dto.CreatedAt),
          updatedAt: new Date(dto.UpdatedAt),
          createdBy: dto.CreatedBy,
          updatedBy: dto.UpdatedBy
        }));
        this.totalItems = response.pagination.totalCount;
        this.totalPages = response.pagination.totalPages;
        // Não sobrescrever currentPage após resposta - usar o valor já setado
        // this.currentPage = response.pagination.currentPage;
        this.loading = false;
      },
      error: (error) => {
        console.error('❌ Erro ao carregar grupos:', error);
        this.errorMessage = 'Erro ao carregar grupos';
        this.errorService.showError('Erro ao carregar grupos');
        this.loading = false;
      }
    });
  }

  /**
   * Busca grupos por termo
   */
  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    
    if (searchTerm.trim() === '') {
      this.loadGroups();
    } else {
      this.loading = true;
      this.groupsService.searchGroupsByName(searchTerm).subscribe({
        next: (groups) => {
          this.groups = groups;
          this.totalItems = groups.length;
          this.totalPages = Math.ceil(groups.length / this.pageSize);
          this.loading = false;
        },
        error: (error) => {
          console.error('❌ Erro ao buscar grupos:', error);
          this.errorService.showError('Erro ao buscar grupos');
          this.loading = false;
        }
      });
    }
  }

  /**
   * Manipula mudança de página
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadGroups();
  }

  /**
   * Manipula ações da tabela
   */
  handleAction(event: { action: string; item: Group }): void {
    const { action, item } = event;

    switch (action) {
      case 'edit':
        this.editGroup(item);
        break;
      case 'delete':
        this.deleteGroup(item);
        break;
      default:
        break;
    }
  }

  /**
   * Edita um grupo
   */
  editGroup(group: Group): void {
    const name = prompt('Nome do grupo:', group.name);
    if (name === null) return;
    
    if (!name.trim()) {
      alert('Por favor, preencha o nome');
      return;
    }
    
    const description = prompt('Descrição:', group.description || '');
    if (description === null) return;

    const currentUser = this.authService.currentUserValue;
    const userEmail = currentUser?.email || 'unknown@user.com';

    const updateData = {
      name: name,
      description: description || '',
      updatedBy: userEmail
    };

    this.groupsService.updateGroup(group.id, updateData).subscribe({
      next: () => {
        alert('Grupo atualizado com sucesso!');
        this.loadGroups();
      },
      error: (error) => {
        console.error('Erro ao atualizar grupo:', error);
        this.errorService.showError('Erro ao atualizar grupo');
      }
    });
  }

  /**
   * Exclui um grupo
   */
  deleteGroup(group: Group): void {
    if (confirm(`Deseja realmente excluir o grupo "${group.name}"?\n\nEsta ação não pode ser desfeita.`)) {
      const currentUser = this.authService.currentUserValue;
      const userEmail = currentUser?.email || 'unknown@user.com';
      
      /*
      this.groupsService.deleteGroup(group.id, userEmail).subscribe({
        next: () => {
          alert('Grupo excluído com sucesso!');
          this.loadGroups();
        },
        error: (error) => {
          console.error('Erro ao excluir grupo:', error);
          
          // Mensagem específica se o grupo tiver dependências
          let errorMessage = 'Erro ao excluir grupo';
          if (error.status === 400 || error.status === 409) {
            errorMessage = 'Não é possível excluir este grupo pois ele possui permissões ou está associado a perfis.';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }
          
          this.errorService.showError(errorMessage);
          alert(errorMessage);
        }
      });
      */
      alert('Você não possui permissão para excluir grupos neste momento.');
    }
  }

  /**
   * Navega para a tela de criação de grupo
   */
  createGroup(): void {
    this.router.navigate(['/admin/groups/new']);
  }

  /**
   * Formata o ProfileId
   */
  formatProfileId(profileId: number | null): string {
    if (profileId === 0 || profileId === null || profileId === undefined) {
      return 'Independente';
    }
    return profileId.toString();
  }

  /**
   * Formata data para exibição
   */
  formatDate(date: Date | string | null): string {
    if (!date) return 'N/A';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Data inválida';
    
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
